<template>
  <v-container class="grey-lighten-3 full-height">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <v-sheet class="report-paper pa-6" elevation="3" id="printable-area">

          <!-- Header (UNCHANGED) -->
          <div class="page-header">
            <div class="header-section d-flex justify-space-between align-start">
              <div class="company-info">
                <h4 class="company-name">{{ order.branch?.company?.name }}</h4>
                <p class="company-text">{{ order.branch?.company?.address }}</p>
                <p class="company-text">
                  <v-icon size="small">mdi-phone-outline</v-icon>
                  {{ order.branch?.company?.mobile }}
                </p>
                <p class="company-text">
                  <v-icon size="small">mdi-email-outline</v-icon>
                  {{ order.branch?.company?.email }}
                </p>
                <p v-if="order.branch?.company?.website" class="company-text">
                  <v-icon size="small">mdi-web</v-icon>
                  {{ order.branch?.company?.website }}
                </p>
              </div>

              <div class="logo-container">
                <v-img
                  :src="`${getFileServerUrl}/${order.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                  max-width="150"
                  max-height="70"
                  contain
                />
              </div>
            </div>

            <div class="text-center my-6">
              <h2 class="report-title">QC Report</h2>
            </div>
          </div>

          <!-- Meta Info (QC specific but same box) -->
          <div class="meta-box">
            <div><strong>QC No:</strong> {{ order.code }}</div>
            <div><strong>Status:</strong> {{ order.qc_status }}</div>
            <div><strong>QC Date:</strong> {{ formatDate(order.qc_date) }}</div>
          </div>

          <!-- Details Table (SAME TABLE, DIFFERENT DATA FLOW) -->
          <table class="dep-table order-table mt-4">
            <thead>
              <tr>
                <th>SL</th>
                <th>Style</th>
                <th>Color</th>
                <th>Size</th>
                <th class="text-right">Checked</th>
                <th class="text-right">Passed</th>
                <th class="text-right">Failed</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, i) in order.details" :key="row.id">
                <!-- SL -->
                <td class="text-center"  v-if="i === 0"
                  :rowspan="order.details.length">{{ i + 1 }}</td>

                <!-- MASTER ROWSPAN -->
                <td
                  v-if="i === 0"
                  :rowspan="order.details.length"
                >
                  {{ order.style?.name || '-' }}
                  <br>
                  <small>({{ order.style?.code || '-' }})</small>
                </td>

                <td
                  v-if="i === 0"
                  :rowspan="order.details.length"
                  class="text-center"
                >
                  {{ order.color?.name || '-' }}
                </td>

                <!-- SIZE WISE -->
                <td class="text-center">
                  {{ row.size?.name || '-' }}
                </td>

                <td class="text-right">{{ row.checked_qty }}</td>
                <td class="text-right">{{ row.passed_qty }}</td>
                <td class="text-right">{{ row.failed_qty }}</td>
              </tr>

              <!-- TOTAL ROW -->
              <tr class="font-weight-bold">
                <td colspan="4" class="text-right">Total</td>
                <td class="text-right">{{ order.checked_qty }}</td>
                <td class="text-right">{{ order.passed_qty }}</td>
                <td class="text-right">{{ order.failed_qty }}</td>
              </tr>
            </tbody>
          </table>

        </v-sheet>
      </v-col>
    </v-row>

    <!-- Print Button (UNCHANGED) -->
    <v-row justify="center" class="d-print-none mt-4">
      <v-btn color="primary" prepend-icon="mdi-printer" @click="print">
        Print
      </v-btn>
    </v-row>
  </v-container>
</template>

<script setup>
    import { ref, computed, onMounted } from "vue";
    import { useStore } from "vuex";
    import { useRoute } from "vue-router";

    const store = useStore();
    const route = useRoute();

    const order = ref({ branch: {}, details: [] });
    const getFileServerUrl = computed(() => store.getters.getFileServerUrl);

    function formatDate(date) {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB");
    }

    async function fetchDetails(id) {
    const res = await store.dispatch("getProductionQcById", { id });
    order.value = res.data.data;
    }

    function print() {
        const printContent = document.getElementById("printable-area").innerHTML;
        const printWindow = window.open("", "_blank", `width=${screen.width}, height=${screen.height}`);

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Cutting Report</title>
            <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
            <style>
                body { font-family: 'Times New Roman', serif; margin:0; padding:15px; background:white; color:#000; }
                .report-paper { width:100%; max-width:100%; margin:auto; padding:15px; background:white; }
                .header-section { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #eee; padding-bottom:10px; margin-bottom:16px; }
                .company-name { font-size:20px; font-weight:bold; text-transform:uppercase; margin:0 0 4px 0; }
                .company-text { font-size:14px; margin:2px 0; display:flex; align-items:center; }
                .logo-container img { max-height:70px; max-width:150px; }
                .report-title { font-size:20px; text-align:center; margin:16px 0; }
                .meta-box { border:1px solid #000; padding:8px; font-size:13px; margin-bottom:16px; }
                .dep-table { width:100%; border-collapse:collapse; font-size:13px; margin-bottom:24px; }
                .dep-table th, .dep-table td { border:1px solid #000; padding:6px; vertical-align:middle; }
                .dep-table th { background:#e0e0e0; text-align:center; }
                .text-center { text-align:center; }
                .text-right { text-align:right; }

                @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .page-header { display:table-header-group; }
                .dep-table thead { display:table-header-group; }
                }
            </style>
            </head>
            <body>
            ${printContent}
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    }

    onMounted(() => fetchDetails(route.params.id));
</script>

<style scoped>
    .full-height { min-height: 100vh; }
    .grey-lighten-3 { background-color: #eeeeee !important; }
    .report-paper { background:white; }
    .company-name { font-size:20px; font-weight:bold; text-transform:uppercase; }
    .company-text { font-size:14px; }
    .report-title { font-size:20px; text-align:center; margin:16px 0; }
    .meta-box { border:1px solid #000; padding:8px; font-size:13px; margin-bottom:16px; }
    .dep-table { width:100%; border-collapse:collapse; font-size:13px; margin-bottom:24px; }
    .dep-table th, .dep-table td { border:1px solid #000; padding:6px; vertical-align:middle; }
    .dep-table th { background:#e0e0e0; text-align:center; }
    .text-center { text-align:center; }
    .text-right { text-align:right; }
</style>
